import React from "react";

import {
  Alert,
  Button,
  FormControl,
  Grid,
  InputLabel,
  SnackbarOrigin,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import QRCode from "qrcode";
import PleaseNote from "src/components/wallets/Modal/deposit/PleaseNote";
import SecondaryButton from "src/components/wallets/Modal/deposit/SecondaryButton";
import SecondaryInputField from "src/components/wallets/Modal/deposit/SecondaryInputField";
import DepositMinimumDisclaimer from "../../common/DepositMinimumDisclaimer";
import CopyIcon from "src/icons/CopyIcon";
import { CloseButton, StyledSnackbar } from "./StyledDeposit";
import CrossIcon from "src/icons/CrossIcon";
const PLEASE_MAKE_SURE_TEXT = (network: string): string =>
  `Please make sure to send your deposit in the network of ${network}. Any deposits not sent in this network cannot be recovered.`;

interface FbDepositCryptoProps {
  showDepositAddresses: boolean;
  onShowDepositAddressChange: () => void;
  depositAddress: string;
  depositNetwork: string;
  depositMemo: string;
  isFbAsset: boolean;
  tokenCode: string;
  minimumDeposit?: { hasMinimum: boolean; message: string };
}

const FbDepositCrypto = (props: FbDepositCryptoProps): JSX.Element => {
  const {
    depositAddress,
    depositNetwork,
    depositMemo,
    showDepositAddresses,
    onShowDepositAddressChange,
    isFbAsset,
    tokenCode,
    minimumDeposit,
  } = props;

  interface State extends SnackbarOrigin {
    open: boolean;
  }

  // ================= Component variables =================
  const QRCodeHeight = isFbAsset ? "210px" : "180px";
  const [qrCode, setQRCode] = React.useState<string>("");

  const theme = useTheme();

  const [snackBarstate, setSnackbarState] = React.useState<State>({
    open: false,
    vertical: "bottom",
    horizontal: "left",
  });
  const { vertical, horizontal, open } = snackBarstate;
  // ================= Generate QR Code & Functions =================
  const generateQR = async (text: string | undefined): Promise<void> => {
    if (text) {
      try {
        const res = await QRCode.toDataURL(text);
        setQRCode(res);
      } catch (err) {
        console.warn(err);
      }
    }
  };

  // execute generation of qr code
  generateQR(depositAddress);

  // copy address to clipboard and activate tooltip
  const copyToClipboard = (text: string): void => {
    navigator.clipboard.writeText(text);
    setSnackbarState({ ...snackBarstate, open: true });
    setTimeout((): void => {
      setSnackbarState({ ...snackBarstate, open: false });
    }, 1000);
  };

  return (
    <Stack spacing={2}>
      {/* if show deposit address is false and token is not an fb asset -> show please note component */}
      {!showDepositAddresses && !isFbAsset ? (
        <Stack spacing={1}>
          {/* PLEASE NOTE COMPONENT */}
          <PleaseNote />

          {/* SHOW DEPOSIT ADDRESS BUTTON */}
          <SecondaryButton boxShadow onClick={onShowDepositAddressChange}>
            Show deposit address
          </SecondaryButton>
        </Stack>
      ) : (
        <Stack spacing={1}>
          {/* QR CODE COMPONENT */}
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            item
            sx={{ height: QRCodeHeight }}>
            <img src={qrCode} alt={depositAddress} height="100%" />
          </Grid>

          <InputLabel shrink htmlFor="memo-input">
            Deposit Address
          </InputLabel>
          {/* DEPOSIT ADDRESS COMPONENT */}
          <SecondaryInputField
            disabled={true}
            defaultValue={depositAddress}
            inputType="text"
            id="deposit-address-input"
            endAdornment={
              true && (
                <>
                  <Button
                    onClick={(): void => copyToClipboard(depositAddress)}
                    endIcon={<CopyIcon />}
                    sx={{
                      backgroundColor: theme.palette.secondary.main,
                      color: "#fff",
                      textTransform: "capitalize",
                      paddingRight: "15px",
                      paddingLeft: "15px",
                    }}>
                    Copy
                  </Button>
                </>
              )
            }
            sx={{
              backgroundColor: "#F8F9F9",
              fontWeight: 700,
              textOverflow: "clip ellipsis clip 0 3ch",
            }}
          />
        </Stack>
      )}

      <StyledSnackbar
        anchorOrigin={{ vertical, horizontal }}
        open={open}
        onClose={() => setSnackbarState({ ...snackBarstate, open: false })}
        autoHideDuration={1000}
        message={"Copied to clipboard"}
        key={vertical + horizontal}
        action={
          <CloseButton
            size="small"
            onClick={() => setSnackbarState({ ...snackBarstate, open: false })}>
            <CrossIcon height={"15"} width={"15"} />
          </CloseButton>
        }
      />

      {/* MEMO INPUT FIELD */}
      {depositMemo && (
        <FormControl variant="standard">
          <div onClick={(): void => copyToClipboard(depositMemo)} style={{ cursor: "pointer" }}>
            <InputLabel shrink htmlFor="memo-input">
              Memo/Destination Tag
            </InputLabel>
            <SecondaryInputField
              fullWidth
              value={depositMemo}
              inputType="text"
              id="deposit-memo-input"
              endAdornment={
                <Button
                  endIcon={<CopyIcon />}
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    color: "#fff",
                    textTransform: "capitalize",
                    paddingRight: "15px",
                    paddingLeft: "15px",
                  }}>
                  Copy
                </Button>
              }
              sx={{ backgroundColor: "#F8F9F9", width: "100%", pointerEvents: "none" }}
            />
          </div>

          {/* DO NOT REMOVE! NEEDED FOR LEGAL REQUIREMENTS */}
          <Alert severity="warning" sx={{ marginTop: "5px" }}>
            <Typography style={{ fontSize: "12px" }}>
              Please make sure to include the memo above. Any deposits sent without the memo cannot
              be recovered.
            </Typography>
          </Alert>
        </FormControl>
      )}
      {minimumDeposit?.hasMinimum && (
        <FormControl variant="standard">
          <InputLabel shrink htmlFor="minimum-input">
            Minimum deposit
          </InputLabel>
          <SecondaryInputField
            disabled={true}
            value={minimumDeposit.message}
            inputType="text"
            id="minimum-format-input"
            sx={{ backgroundColor: "#F8F9F9" }}
          />
        </FormControl>
      )}
      <FormControl variant="standard">
        <InputLabel shrink htmlFor="format-input">
          Network
        </InputLabel>
        <SecondaryInputField
          disabled={true}
          value={depositNetwork}
          inputType="text"
          id="deposit-network-input"
          sx={{ backgroundColor: "#F8F9F9" }}
        />
      </FormControl>

      {/* PLEASE MAKE SURE TEXT */}
      <Typography sx={{ fontSize: "12px", lineHeight: "14px", color: "#7B858B" }}>
        {PLEASE_MAKE_SURE_TEXT(depositNetwork)}
      </Typography>
      <DepositMinimumDisclaimer tokenCode={tokenCode} />
    </Stack>
  );
};

export default FbDepositCrypto;
